// Direct port of ~/Desktop/calyx-erp/src/components/deals/DealSubRow/DealSubRow.tsx
// re-wired to React Query mutations (status / void / restore) instead of the
// prototype's reducer-based useDeals() hook.

import React, { Fragment, useCallback, useMemo, useState } from 'react'

import type { Deal } from '../types/deal'
import type { Invoice } from '../types/invoice'
import { useToast } from '../../../hooks/useToast'
import { useInvoiceEditHandlers } from '../hooks/useInvoiceEditHandlers'
import { useInvoicesQuery } from '../hooks/useInvoicesQuery'
import { useInvoiceStatusActions } from '../hooks/useInvoiceStatusActions'
import { useShipmentsQuery } from '../hooks/useShipmentsQuery'
import { useModal } from '../state/ModalProvider'

import { AddInvoiceForm } from './AddInvoiceForm'
import { FilterBar } from './FilterBar'
import type { InvFilter } from './FilterBar'
import { InvoiceRow } from './InvoiceRow'
import { InvoiceTableHeader } from './InvoiceTableHeader'
import { VoidedSummaryRow } from './VoidedSummaryRow'

interface Props {
  deal: Deal
}

export const DealSubRow: React.FunctionComponent<Props> = (props) => {
  const { deal } = props
  const toast = useToast()
  const { openSendInvoice, openInvoicePreview } = useModal()
  const invoicesQuery = useInvoicesQuery(deal.id)
  const shipmentsQuery = useShipmentsQuery(deal.id)

  const invoices = invoicesQuery.data ?? []
  const shipments = shipmentsQuery.data ?? []

  const [filter, setFilter] = useState<InvFilter>('all')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [focusPaidFor, setFocusPaidFor] = useState<string | null>(null)
  const [pendingPartialFor, setPendingPartialFor] = useState<string | null>(null)
  const [voidedCollapsed, setVoidedCollapsed] = useState(true)
  const [showAddForm, setShowAddForm] = useState(false)

  const onPartialSelected = useCallback((invoice: Invoice) => {
    setPendingPartialFor(invoice.invoiceNumber)
    setExpanded((current) => {
      if (current.has(invoice.invoiceNumber)) return current
      const next = new Set(current)
      next.add(invoice.invoiceNumber)
      return next
    })
    setFocusPaidFor(invoice.invoiceNumber)
  }, [])

  const { applyStatusChange, voidInvoice, restoreInvoice } = useInvoiceStatusActions(deal.id, {
    onPartialSelected,
  })
  const edit = useInvoiceEditHandlers(deal.id)

  const filtered = useMemo(() => {
    return invoices.filter((invoice) => {
      if (filter === 'auto') return invoice.origin !== 'manual'
      if (filter === 'manual') return invoice.origin === 'manual'
      return true
    })
  }, [invoices, filter])

  const voidedCount = useMemo(
    () => filtered.filter((invoice) => invoice.status === 'Voided').length,
    [filtered],
  )

  const hiddenCount = invoices.length - filtered.length

  const toggleRow = useCallback((invoiceId: string) => {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(invoiceId)) next.delete(invoiceId)
      else next.add(invoiceId)
      return next
    })
  }, [])

  const hasShipmentFor = useCallback(
    (invoice: Invoice) =>
      shipments.some((shipment) => shipment.linkedInvoiceNumber === invoice.invoiceNumber) ||
      (Array.isArray(invoice.linkedShipmentIds) && invoice.linkedShipmentIds.length > 0),
    [shipments],
  )

  function onClickSend(invoice: Invoice): void {
    openSendInvoice(deal.id, invoice.id)
  }

  function onClickPreview(invoice: Invoice): void {
    openInvoicePreview(deal.id, invoice.id)
  }

  const onShipmentClick = useCallback(() => {
    toast.current?.show({
      severity: 'info',
      summary: 'Shipment view coming soon',
      detail: 'Edit Deal → Shipment tab is wired up; this jump-to lands in a follow-up.',
      life: 2500,
    })
  }, [toast])

  const onFocusPaidConsumed = useCallback(() => {
    setFocusPaidFor(null)
  }, [])

  const onPaidBlur = useCallback(
    (invoiceNumber: string) => {
      setPendingPartialFor((current) => (current === invoiceNumber ? null : current))
    },
    [],
  )

  const onToggleVoidedCollapsed = useCallback(() => {
    setVoidedCollapsed((value) => !value)
  }, [])

  let summaryEmitted = false

  return (
    <tr className="sub-row open">
      <td colSpan={6}>
        <FilterBar filter={filter} setFilter={setFilter} hiddenCount={hiddenCount} />

        <table className="sub-table-inner">
          <InvoiceTableHeader />
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{ textAlign: 'center', color: 'var(--muted)', padding: '24px 16px' }}
                >
                  No invoices match this filter.
                </td>
              </tr>
            ) : null}
            {filtered.map((invoice) => {
              const isVoided = invoice.status === 'Voided'
              const summaryRow =
                isVoided && !summaryEmitted && voidedCount > 0 ? (
                  <VoidedSummaryRow
                    key={`voided-summary-${invoice.invoiceNumber}`}
                    voidedCount={voidedCount}
                    isCollapsed={voidedCollapsed}
                    onToggle={onToggleVoidedCollapsed}
                  />
                ) : null
              if (summaryRow) summaryEmitted = true

              if (isVoided && voidedCollapsed) {
                return summaryRow ?? null
              }

              return (
                <Fragment key={`row-frag-${invoice.invoiceNumber}`}>
                  {summaryRow}
                  <InvoiceRow
                    invoice={invoice}
                    deal={deal}
                    invoiceIndex={invoices.indexOf(invoice)}
                    invoices={invoices}
                    editingState={{
                      isExpanded: expanded.has(invoice.invoiceNumber),
                      isPendingPartial: pendingPartialFor === invoice.invoiceNumber,
                      focusPaid: focusPaidFor === invoice.invoiceNumber,
                      isEditingPeriod: edit.editingPeriodFor === invoice.invoiceNumber,
                      periodDraft: edit.periodDraft,
                      isEditingQuote: edit.editingQuoteFor === invoice.invoiceNumber,
                      quoteDraft: edit.quoteDraft,
                    }}
                    handlers={{
                      toggleRow,
                      applyStatusChange,
                      voidInvoice,
                      restoreInvoice,
                      setPeriodDraft: edit.setPeriodDraft,
                      startEditPeriod: edit.startEditPeriod,
                      cancelEditPeriod: edit.cancelEditPeriod,
                      saveEditPeriod: edit.saveEditPeriod,
                      setQuoteDraft: edit.setQuoteDraft,
                      startEditQuote: edit.startEditQuote,
                      cancelEditQuote: edit.cancelEditQuote,
                      saveEditQuote: edit.saveEditQuote,
                      onClickSend,
                      onClickPreview,
                      onFocusPaidConsumed,
                      onPaidBlur: () => onPaidBlur(invoice.invoiceNumber),
                      onShipmentClick,
                    }}
                    uiFlags={{ showShipment: hasShipmentFor(invoice) }}
                  />
                </Fragment>
              )
            })}
          </tbody>
        </table>
        <div className="addon-area">
          {!showAddForm ? (
            <button
              type="button"
              className="btn-text"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              onClick={() => setShowAddForm(true)}
            >
              <svg
                width={13}
                height={13}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              Manually add invoice
            </button>
          ) : (
            <AddInvoiceForm deal={deal} onClose={() => setShowAddForm(false)} />
          )}
        </div>
      </td>
    </tr>
  )
}
